import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Form,
  Spinner,
  Button,
  Alert,
  Modal,
} from "react-bootstrap";
import { toast } from "react-toastify";
import moment from "moment";
import { useBreadcrumb } from "../../data/BreadcrumbHook";
import { useProperty } from "../../data/PropertyHook";
import { useWorkOrders } from "../../data/WorkOrdersHook";
import { useWorkOrderItems } from "../../data/WorkItemHook";
import { useAuth } from "../../data/AuthHook";
import useLogger from "../../util/Logger";
import WorkOrderActiveWorkItems from "./WorkOrderActiveWorkItems";
import WorkOrderCompletedWorkItems from "./WorkOrderCompletedWorkItems";
import "./WorkOrder.css";
import { isWorkOrderOpen } from "../../data/WorkOrderStatus";
import SpinnerButton from "../shared/Spinnerbutton";
import AccessNotes from "../shared/AccessNotes";
import { WORK_ORDER_CANCELLATION_REASONS } from "../../util/CancellationReasons";

function WorkOrder() {
  const logger = useLogger();
  const navigate = useNavigate();
  const breadcrumbRepository = useBreadcrumb();
  const propertyRepository = useProperty();
  const workOrdersRepository = useWorkOrders();
  const workOrderItemsRepository = useWorkOrderItems();
  const auth = useAuth();
  const cancelledBy = `${auth?.user?.idToken?.payload?.given_name ?? ""} ${auth?.user?.idToken?.payload?.family_name ?? ""}`.trim();


  const { propertyId, workOrderId } = useParams();

  const [property, setProperty] = useState(null);
  const [workOrder, setWorkOrder] = useState(null);

  const [isOpen, setIsOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelReasonError, setCancelReasonError] = useState("");

  useEffect(() => {
    const getData = async () => {
      try {
        logger.debug("WorkOrder.useEffect", `Setting breadcrumb`);
        breadcrumbRepository.setBreadcrumb([
          { route: "/", name: "search" },
          { route: `/property/${propertyId}`, name: "property" },
          {
            route: `/property/${propertyId}/workorder/${workOrderId}`,
            name: "work order",
          },
        ]);

        logger.debug("WorkOrder.useEffect", `Getting data`);

        const [retrievedProperty, retrievedWorkOrders] = await Promise.all([
          propertyRepository.getProperty(propertyId),
          workOrdersRepository.getWorkOrders(propertyId),
        ]);

        const retrievedWorkOrder = retrievedWorkOrders.find(
          ({ id }) => id === workOrderId
        );

        if (!retrievedWorkOrder)
          throw new Error(
            `The work order Id '${workOrderId}' did not match a work order on property '${propertyId}'`
          );

        setProperty(retrievedProperty);
        setWorkOrder(retrievedWorkOrder);
        setIsOpen(isWorkOrderOpen(retrievedWorkOrder));
      } catch (error) {
        logger.error(
          "WorkOrder.useEffect",
          `An error occurred while loading work order '${workOrderId}' for property '${propertyId}' ${error}`
        );
        toast.error(
          "An error occurred while loading the work order. Please try again."
        );
        navigate("/");
      }
    };
    getData();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const closeCancelModal = () => {
    setShowCancelModal(false);
    setCancelReason("");
    setCancelReasonError("");
  };

  const cancelWorkOrder = async (reason) => {
    try {
      setCancelling(true);

      const uncompleteWorkOrders = workOrder.workItems.filter(
        ({ completed }) => !completed
      );

      await Promise.all(
        uncompleteWorkOrders.map(async (workItem) => {
         await workOrderItemsRepository.cancelWorkItem(
            workItem.id,
            workItem.workitemtemplateid,
            reason,
            cancelledBy,
            workItem.subCategory
          );
        })
      );

      await workOrdersRepository.cancelWorkOrder(workOrder.reference);

      closeCancelModal();
      // Redirect
      navigate(`/property/${property.id}`);
      toast.success("The Work Order was cancelled successfully.");
    } catch (err) {
      toast.error("The Work Order could not be cancelled. Please try again.");
    }

    setCancelling(false);
  };

  const submitCancelWorkOrder = async () => {
    if (!cancelReason) {
      setCancelReasonError("Select a cancellation reason.");
      return;
    }

    setCancelReasonError("");
    await cancelWorkOrder(cancelReason);
  };

  const view =
    !property || !workOrder ? (
      <Container className="coreContent">
        <Spinner
          animation="border"
          role="status"
          style={{
            marginTop: "200px",
            marginLeft: "50%",
            marginBottom: "200px",
          }}
        >
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </Container>
    ) : (
      <Container className="coreContent">
        <h2>Work Order</h2>
        <Row className="mb-4">
          <Col xs={2}>
            <Form.Label className="form-label-heading">Property Id:</Form.Label>
          </Col>
          <Col xs={4}>
            <Form.Label>{property.id}</Form.Label>
          </Col>
        </Row>
        <Row className="mt-1">
          <Col xs={2}>
            <Form.Label className="form-label-heading">Address</Form.Label>
          </Col>
          <Col xs={6}>
            <Form.Control type="text" readOnly value={property.fullAddress} />
          </Col>
        </Row>
        <Row className="mt-1">
          <Col xs={2}>
            <Form.Label className="form-label-heading">Postcode</Form.Label>
          </Col>
          <Col xs={2}>
            <Form.Control type="text" readOnly value={property.postCode} />
          </Col>
        </Row>
        <Row className="mt-4">
          <Col xs={2}>
            <Form.Label className="form-label-heading">Site</Form.Label>
          </Col>
          <Col xs={6}>
            <Form.Control type="text" readOnly value={property.siteName} />
          </Col>
        </Row>
        <Row className="mt-4">
          <Col xs={2}>
            <Form.Label className="form-label-heading">Access Notes</Form.Label>
          </Col>
          <Col xs={6}>
            <AccessNotes accessNotes={property.accessNotes} />
          </Col>
        </Row>
        <Row className="mt-4">
          <Col xs={2}>
            <Form.Label className="form-label-heading">
              Property Access Notes
            </Form.Label>
          </Col>
          <Col xs={6}>
            <Form.Control
              as="textarea"
              readOnly
              value={property.propertyAccessNotes}
            />
          </Col>
        </Row>
        {property.redFlag && (
          <Row className="mt-4">
            <Col xs={8}>
              <Alert variant="danger">
                Caution - This Property has a Red Flag set on it.
              </Alert>
            </Col>
          </Row>
        )}
        <Row className="mt-4">
          <Col xs={3}>
            <Form.Label className="form-label-heading">
              Work Order Reference
            </Form.Label>
          </Col>
          <Col xs={3}>
            <Form.Label className="form-label-heading">Created</Form.Label>
          </Col>
          <Col xs={3}>
            <Form.Label className="form-label-heading">Type</Form.Label>
          </Col>
          <Col xs={3}>
            <Form.Label className="form-label-heading">Category</Form.Label>
          </Col>
        </Row>
        <Row className="mt-1">
          <Col xs={3}>
            <Form.Control type="text" readOnly value={workOrder.reference} />
          </Col>
          <Col xs={3}>
            <Form.Control
              type="text"
              readOnly
              value={
                workOrder.createdOn
                  ? moment(workOrder.createdOn).format("DD/MM/YYYY h:mm a")
                  : ""
              }
            />
          </Col>
          <Col xs={3}>
            <Form.Control type="text" readOnly value={workOrder.type} />
          </Col>
          <Col xs={3}>
            <Form.Control type="text" readOnly value={workOrder.category} />
          </Col>
        </Row>
        <Row className="mt-4">
          <Col xs={6}>
            <Form.Label className="form-label-heading">Description</Form.Label>
          </Col>
          <Col xs={6}>
            <Form.Label className="form-label-heading">Diagnosis</Form.Label>
          </Col>
        </Row>
        <Row className="mt-1">
          <Col xs={6}>
            <Form.Control
              type="textarea"
              readOnly
              value={workOrder.description}
            />
          </Col>
          <Col xs={6}>
            <Form.Control
              type="textarea"
              readOnly
              value={workOrder.diagnosis}
            />
          </Col>
        </Row>
        <Row className="mt-4">
          <Col xs={3}>
            <Form.Label className="form-label-heading">
              Reporter Name
            </Form.Label>
          </Col>
          <Col xs={3}>
            <Form.Label className="form-label-heading">
              Reporter Type
            </Form.Label>
          </Col>
          <Col xs={3}>
            <Form.Label className="form-label-heading">
              Reporter Telephone
            </Form.Label>
          </Col>
          <Col xs={3}>
            <Form.Label className="form-label-heading">
              Reporter Email
            </Form.Label>
          </Col>
        </Row>
        <Row className="mt-1">
          <Col xs={3}>
            <Form.Control type="text" readOnly value={workOrder.reporterName} />
          </Col>
          <Col xs={3}>
            <Form.Control type="text" readOnly value={workOrder.reporterType} />
          </Col>
          <Col xs={3}>
            <Form.Control
              type="text"
              readOnly
              value={workOrder.reporterTelephone}
            />
          </Col>
          <Col xs={3}>
            <Form.Control
              type="text"
              readOnly
              value={workOrder.reporterEmail}
            />
          </Col>
        </Row>
        <Row className="mt-4">
          <Col xs={3}>
            <Form.Label className="form-label-heading">
              Customer Name
            </Form.Label>
          </Col>
          <Col xs={3}>
            <Form.Label className="form-label-heading">
              Customer Telephone
            </Form.Label>
          </Col>
          <Col xs={3}>
            <Form.Label className="form-label-heading">
              Customer Email
            </Form.Label>
          </Col>
        </Row>
        <Row className="mt-1">
          <Col xs={3}>
            <Form.Control type="text" readOnly value={workOrder.customerName} />
          </Col>
          <Col xs={3}>
            <Form.Control
              type="text"
              readOnly
              value={workOrder.customerTelephone}
            />
          </Col>
          <Col xs={3}>
            <Form.Control
              type="text"
              readOnly
              value={workOrder.customerEmail}
            />
          </Col>
        </Row>
        {isOpen && (
          <Row className="mt-4 work-order-action-container">
            <Col>
              {workOrder.type === "Reactive" && (
                <>
                  <Link
                    to={`/property/${propertyId}/workorder/${workOrderId}/update`}
                  >
                    <Button variant="secondary">Update Work Order</Button>
                  </Link>
                  <Button
                    variant="danger"
                    onClick={() => setShowCancelModal(true)}
                    disabled={cancelling}
                  >
                    Cancel Work Order
                  </Button>
                </>
              )}
              {(workOrder.type === "Reactive" ||
                workOrder.type === "Maintenance" ||
                (workOrder.type === "Project" &&
                  workOrder.category === "CMV")) &&
                workOrder.workItems.filter(({ completed }) => !completed)
                  .length === 0 && (
                  <Link
                    to={`/property/${propertyId}/workorder/${workOrderId}/workitem/create`}
                  >
                    <Button variant="secondary">Create Work Item</Button>
                  </Link>
                )}
            </Col>
          </Row>
        )}
        {isOpen && (
          <Modal show={showCancelModal} onHide={closeCancelModal} centered>
            <Modal.Header closeButton>
              <Modal.Title>Cancel Work Order</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Form.Group controlId="work-order-cancel-reason">
                <Form.Label>Cancellation Reason</Form.Label>
                <Form.Select
                  value={cancelReason}
                  onChange={(event) => {
                    setCancelReason(event.target.value);
                    setCancelReasonError("");
                  }}
                  isInvalid={Boolean(cancelReasonError)}
                >
                  <option value="">Select a reason</option>
                  {WORK_ORDER_CANCELLATION_REASONS.map((reason) => (
                    <option key={reason} value={reason}>
                      {reason}
                    </option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {cancelReasonError}
                </Form.Control.Feedback>
              </Form.Group>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={closeCancelModal}>
                Back
              </Button>
              <SpinnerButton
                text="Confirm Cancel"
                variant="danger"
                action={submitCancelWorkOrder}
                waiting={cancelling}
              />
            </Modal.Footer>
          </Modal>
        )}
        <Row className="mt-4">
          <Col>
            <Form.Label className="form-label-heading">
              Active Work Items
            </Form.Label>
          </Col>
        </Row>
        <Row className="mt-1">
          <Col>
            <WorkOrderActiveWorkItems workOrder={workOrder} />
          </Col>
        </Row>
        <Row className="mt-4">
          <Col>
            <Form.Label className="form-label-heading">
              Completed Work Items
            </Form.Label>
          </Col>
        </Row>
        <Row className="mt-1 mb-4">
          <Col>
            <WorkOrderCompletedWorkItems
              property={property}
              workOrder={workOrder}
            />
          </Col>
        </Row>
        <Row className="mt-4 mb-4">
          <Col>
            <Link to={`/property/${property.id}`}>
              <Button variant="secondary">Back to property</Button>
            </Link>
          </Col>
        </Row>
      </Container>
    );
  return view;
}

export default WorkOrder;
